import { renderToString } from "react-dom/server";
import { getTexts } from "./texts";
import {
  TilsynserklaeringFormValues,
  TilsynserklaeringMetadata,
} from "../../interfaces/tilsynserklaering";
import { TILSYN_VIRK_FORMAAL } from "../../constants/tilsyn/lists";
import { Fragment } from "react/jsx-runtime";

export function getHtmlResult(
  values: TilsynserklaeringFormValues,
  metadata: TilsynserklaeringMetadata,
) {
  return renderToString(
    <HtmlResult values={values} metadata={metadata} />,
  ).replaceAll("<!-- -->", "");
}

export default function HtmlResult(props: {
  values: TilsynserklaeringFormValues;
  metadata: TilsynserklaeringMetadata;
}) {
  const { values, metadata } = props;

  const texts = getTexts(values, metadata);
  const section4_1 = texts.section4.section4_1;
  const section4_2 = texts.section4.section4_2;
  const section4_2_filteredOptions = section4_2.question.options.filter(
    (option) => values[option.value] === true,
  );
  const section4_3 = texts.section4.section4_3;
  const section4_4 = texts.section4.section4_4;
  const section4_5 = texts.section4.section4_5;
  const section4_6 = texts.section4.section4_6;
  const section4_7 = texts.section4.section4_7;
  const section4_8 = texts.section4.section4_8;
  const section4_9 = texts.section4.section4_9;
  const section4_10 = texts.section4.section4_10;
  const section4_11 = texts.section4.section4_11;
  const section4_12 = texts.section4.section4_12;
  const section4_13 = texts.section4.section4_13;
  const section4_14 = texts.section4.section4_14;
  const section4_14a = texts.section4.section4_14a;
  const section4_15 = texts.section4.section4_15;
  const section4_15a = texts.section4.section4_15a;
  const section4_16 = texts.section4.section4_16;

  return (
    <div className="as-document">
      <h1>Erklæring til brug for Advokatsamfundets tilsyn</h1>
      <p>
        <small>
          Tidspunkt: {new Date().toLocaleDateString("DA-dk")} kl.{" "}
          {new Date().toLocaleTimeString("DA-dk", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </small>
      </p>
      <section>
        <h2>{texts.section1.title}</h2>
        <h3>{texts.section1.section1_1.title}</h3>
        <h4>{texts.section1.section1_1.question.label}</h4>
        <p>
          {texts.section1.section1_1.question.getAnswerToQuestion(
            values[texts.section1.section1_1.question.id],
          )}
        </p>
        <h3>{texts.section1.section1_2.title}</h3>
        <div>
          <h4>{texts.section1.section1_2.question.label}</h4>
          <p>{values[texts.section1.section1_2.question.id]?.navn ?? "-"}</p>
        </div>

        {values.nuvaerendeForsikringsselskab?.id === "andet" && (
          <div>
            <h4>{texts.section1.section1_2.question2.label}</h4>
            <p>
              {values[texts.section1.section1_2.question2.id]?.length > 0
                ? values[texts.section1.section1_2.question2.id]
                : "-"}
            </p>
          </div>
        )}
        <div>
          <h4>{texts.section1.section1_2.question3.label}</h4>
          <p>
            {values[texts.section1.section1_2.question3.id].length > 0
              ? values[texts.section1.section1_2.question3.id]
              : "-"}
          </p>
        </div>
      </section>
      <section>
        <h2>{texts.section2.title}</h2>
        <h3>{texts.section2.section2_1.title}</h3>
        <div>
          <h4>{texts.section2.section2_1.question.label}</h4>
          <p>
            {texts.section2.section2_1.question.getAnswerToQuestion(
              values[texts.section2.section2_1.question.id],
            )}
          </p>
          {texts.section2.section2_1.question.getAnswerToQuestion(
            values[texts.section2.section2_1.question.id],
          ) === "Ja" &&
            values.tilsynEjerandele && (
              <>
                <h3>{texts.section2.section2_2.title}</h3>
                <ol>
                  {values.tilsynEjerandele.map((ejerandel) => {
                    const checkedItems = TILSYN_VIRK_FORMAAL.map((e, i) => {
                      const exists =
                        !!ejerandel[`formaal-${(i + 1).toString()}`];
                      return exists ? <dd key={e}>{e}</dd> : null;
                    }).filter((item) => item !== null);
                    const valueVirksomhedensFormaal =
                      checkedItems.length > 0 ? checkedItems : <dd>-</dd>;

                    return (
                      <li key={ejerandel.virksomhedensNavn}>
                        <dl>
                          <div>
                            <dt>
                              <small>Virksomhedsnavn</small>
                            </dt>
                            <dd>
                              {ejerandel.virksomhedensNavn
                                ? ejerandel.virksomhedensNavn
                                : "-"}
                            </dd>
                          </div>
                          <div>
                            <dt>
                              <small>Virksomhedens CVR-nummer</small>
                            </dt>
                            <dd>
                              {ejerandel.virksomhedensCVR
                                ? ejerandel.virksomhedensCVR
                                : "-"}
                            </dd>
                          </div>
                          <div>
                            <dt>
                              <small>Virksomhedens formål</small>
                            </dt>
                            {valueVirksomhedensFormaal}
                          </div>
                          <div>
                            <dt>
                              <small>Ejerandel</small>
                            </dt>
                            <dd>{ejerandel.andel ? ejerandel.andel : "-"}</dd>
                          </div>
                        </dl>
                      </li>
                    );
                  })}
                </ol>
              </>
            )}
        </div>
      </section>
      <section>
        <h2>{texts.section3.title}</h2>
        <h3>{texts.section3.section3_1.title}</h3>
        <div>
          <h4>{texts.section3.section3_1.question.label}</h4>
          <p>
            {texts.section3.section3_1.question.getAnswerToQuestion(
              values[texts.section3.section3_1.question.id],
            )}
          </p>
        </div>
        {texts.section3.section3_1.question.getAnswerToQuestion(
          values[texts.section3.section3_1.question.id],
        ) === "Ja" && (
          <>
            <h3>{texts.section3.section3_1a.title}</h3>
            <div>
              <h4>{texts.section3.section3_1a.question.label}</h4>
              <p>
                {texts.section3.section3_1a.question.getAnswerToQuestion(
                  values[texts.section3.section3_1a.question.id],
                )}
              </p>
            </div>
          </>
        )}
        <h3>{texts.section3.section3_2.title}</h3>
        <div>
          <h4>{texts.section3.section3_2.question.label}</h4>
          <p>
            {texts.section3.section3_2.question.getAnswerToQuestion(
              values[texts.section3.section3_2.question.id],
            )}
          </p>
        </div>
      </section>
      <section>
        <h2>{texts.section4.title}</h2>
        <h3>{section4_1.title}</h3>
        <div>
          <h4>{section4_1.question.label}</h4>
          <p>
            {section4_1.question.getAnswerToQuestion(
              values[section4_1.question.id],
            )}
          </p>
        </div>
        {values["harDuHaftHvidvasksager"] === "ja" && (
          <div>
            <h3>{section4_2.title}</h3>
            <h4>{section4_2.question.label}</h4>
            {section4_2_filteredOptions.length > 0 && (
              <ul>
                {section4_2_filteredOptions.map((option) => (
                  <li key={option.value}>
                    <strong>{option.label}</strong>
                    {values[option.value] && (
                      <Fragment>
                        <h4>{option.inputQuestion.label}</h4>
                        <p>
                          {option.inputQuestion.getAnswerToQuestion(
                            values[option.inputQuestion.id],
                          )}
                          {!values[option.inputQuestion.id] ||
                          values[option.inputQuestion.id].toString().trim() ===
                            ""
                            ? "-"
                            : values[option.inputQuestion.id] === "1" ||
                                values[option.inputQuestion.id] === 1
                              ? " sag"
                              : " sager"}
                        </p>
                      </Fragment>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <h3>{section4_3.title}</h3>
            <div>
              <h4>{section4_3.question.label}</h4>
              <p>
                {section4_3.question.getAnswerToQuestion(
                  values[section4_3.question.id],
                )}
              </p>
            </div>
            <h3>{section4_4.title}</h3>
            <div>
              <h4>{section4_4.question.label}</h4>
              <p>
                {section4_4.question.getAnswerToQuestion(
                  values[texts.section4.section4_4.question.id],
                )}
              </p>
            </div>
            <h3>{section4_5.title}</h3>
            <div>
              <h4>{section4_5.question.label}</h4>
              <p>
                {section4_5.question.getAnswerToQuestion(
                  values[section4_5.question.id],
                )}
              </p>
            </div>
            <h3>{section4_6.title}</h3>
            <div>
              <h4>{section4_6.question.label}</h4>
              <p>
                {section4_6.question.getAnswerToQuestion(
                  values[section4_6.question.id],
                )}
              </p>
            </div>
            <h3>{section4_7.title}</h3>
            <div>
              <h4>{section4_7.question.label}</h4>
              <p>
                {section4_7.question.getAnswerToQuestion(
                  values[section4_7.question.id],
                )}
              </p>
            </div>
            <h3>{section4_8.title}</h3>
            <div>
              <h4>{section4_8.question.label}</h4>
              <p>
                {section4_8.question.getAnswerToQuestion(
                  values[section4_8.question.id],
                )}
              </p>
            </div>
            <h3>{section4_9.title}</h3>
            <div>
              <h4>{section4_9.question.label}</h4>
              <p>
                {section4_9.question.getAnswerToQuestion(
                  values[section4_9.question.id],
                )}
              </p>
            </div>
            <h3>{section4_10.title}</h3>
            <div>
              <h4>{section4_10.question.label}</h4>
              <p>
                {section4_10.question.getAnswerToQuestion(
                  values[section4_10.question.id],
                )}
              </p>
            </div>
            <h3>{section4_11.title}</h3>
            <div>
              <h4>{section4_11.question.label}</h4>
              <p>
                {section4_11.question.getAnswerToQuestion(
                  values[section4_11.question.id],
                )}
              </p>
            </div>
            <h3>{section4_12.title}</h3>
            <div>
              <h4>{section4_12.question.label}</h4>
              <p>
                {section4_12.question.getAnswerToQuestion(
                  values[section4_12.question.id],
                )}
              </p>
            </div>
            <h3>{section4_13.title}</h3>
            <div>
              <h4>{section4_13.question.label}</h4>
              <p>
                {section4_13.question.getAnswerToQuestion(
                  values[section4_13.question.id],
                )}
              </p>
            </div>
            <h3>{section4_14.title}</h3>
            <div>
              <h4>{section4_14.question.label}</h4>
              <p>
                {section4_14.question.getAnswerToQuestion(
                  values[section4_14.question.id],
                )}
              </p>
            </div>
            {values[section4_14.question.id] === "ja" && (
              <>
                <h3>{section4_14a.title}</h3>
                <div>
                  <h4>{section4_14a.question.label}</h4>
                  <p>
                    {section4_14a.question.getAnswerToQuestion(
                      values[section4_14a.question.id],
                    )}
                  </p>
                </div>
              </>
            )}

            {values[section4_14.question.id] !==
              "ikkeBeskaeftigetAdvokatvirksomhed" && (
              <>
                <h3>{section4_15.title}</h3>
                <div>
                  <h4>{section4_15.question.label}</h4>
                  <p>
                    {section4_15.question.getAnswerToQuestion(
                      values[section4_15.question.id],
                    )}
                  </p>
                </div>
                {values[section4_15.question.id] === "ja" && (
                  <>
                    <h3>{section4_15a.title}</h3>
                    <div>
                      <h4>{section4_15a.question.label}</h4>
                      <p>
                        {section4_15a.question.getAnswerToQuestion(
                          values[section4_15a.question.id],
                        )}
                      </p>
                    </div>
                  </>
                )}
                <h3>{section4_16.title}</h3>
                <div>
                  <h4>{section4_16.question.label}</h4>
                  <p>
                    {section4_16.question.getAnswerToQuestion(
                      values[section4_16.question.id],
                    )}
                  </p>
                </div>
              </>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
